/* eslint-disable no-console */
import { supabase } from '../../config/supabase';
import { decimateData, measureDecimation } from '../../utils/dataDecimation';
import { PowerCacheHelper } from '../cache/powerCacheHelper';
import { DEBUG } from '../../config/debug';

// Import extracted modules
import * as powerCalculations from './powerCalculations';
import { getLeverMode } from '../../config/experiments';
import * as strokeAnalysis from './strokeAnalysis';
import * as gpsCalculations from './gpsCalculations';
import * as efficiencyMetrics from './efficiencyMetrics';
import * as dataProcessing from './dataProcessing';

// Import platform contracts
import {
  buildPowerTimeSeriesContract,
  buildGateForceProfileContract,
  type PowerTimeSeriesContract,
  type GateForceProfileContract,
} from '../../platform/contracts';

export interface StrokeData {
  angle: number;
  force: number;
  phase?: string;
  strokeNumber?: number;
  timestamp?: number;
}

export interface GateForceTimeSeries {
  ts: Float32Array;
  angle: Float32Array;
  force: Float32Array;
}

export interface GateForceStrokeSeries {
  strokeIndex: number;
  angle: Float32Array;
  force: Float32Array;
}

export interface GateForceStrokeSamplerRow {
  strokes: GateForceStrokeSeries[];
  skippedStrokes: number;
  totalStrokes: number;
}

export interface GateForceStrokeSamplerResult {
  series: Record<string, GateForceStrokeSamplerRow>;
  meta: {
    targetPoints: number;
    source: 'sampler_v1';
  };
}

export interface GateForceSessionSamplerResult {
  series: Record<string, GateForceTimeSeries>;
  meta: {
    bucketMs: number;
    targetPoints: number;
    source: 'sampler_v1';
  };
}

export interface GateForceProfileSeries {
  angle: Float32Array;
  force: Float32Array;
}

export interface GateForceProfileResult {
  series: Record<string, GateForceProfileSeries>;
  meta: {
    targetPoints: number;
    source: 'sampler_v1';
  };
}

// StrokeGeometryRow interface removed - stroke_geometry table was created based on false assumptions
// Arc metrics are now calculated client-side from telemetry_data via useArcMetrics hook

const SAMPLER_SOURCE = 'sampler_v1' as const;

const toFloat32Array = (values?: unknown[] | null): Float32Array => {
  if (!Array.isArray(values) || values.length === 0) {
    return new Float32Array();
  }

  const array = new Float32Array(values.length);
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    array[i] = typeof value === 'number' ? value : Number(value ?? 0);
  }
  return array;
};

const buildRowerKey = (rowerPosition: number): string => `Rower ${rowerPosition}`;

type UniformSamplerRow = {
  rower_position: number;
  bucket_ms: number | null;
  ts_ms: number[] | null;
  angle_deg: number[] | null;
  force_kgf: number[] | null;
};

type StrokeSamplerRow = {
  rower_position: number;
  stroke_index: number;
  angle_deg: number[] | null;
  force_kgf: number[] | null;
  skipped_strokes: number | null;
  total_strokes: number | null;
};

type MedianProfileRow = {
  rower_position: number;
  angle_deg: number[] | null;
  force_kgf: number[] | null;
};

export interface SessionInfo {
  id: string;
  sessionId: string;
  sessionName?: string; // Add session name field
  crewName?: string; // Add crew name field
  sessionDate: string;
  startTime: string;
  location: string;
  boatType: string;
  file_size?: number;
  created_at: string;
  isDemo?: boolean;
  isPartial?: boolean;
  coach_id?: string; // Coach who owns this session
  team_id?: string | null; // Team association for roster mapping
  team_name?: string | null; // Team name for display
  boat_id?: string | null; // Boat association
  boat_name?: string | null; // Boat name for display
  seat_number?: number | null;
  roster_status?: 'fully-mapped' | 'partially-mapped' | 'unmapped';
  roster_mapped?: number;
  roster_total?: number;
  archived_at?: string | null;
  archived_by?: string | null;
}

/** Global (no team) sessions: eligible for coach permanent delete in archived UI only. */
export function canCoachHardDeleteSession(session: Pick<SessionInfo, 'team_id'>): boolean {
  return session.team_id == null || session.team_id === undefined;
}

/** GLY-383: sessions-list Redis keys must include the authenticated user id. */
export function sessionsListCacheKey(kind: 'active' | 'archived', userId: string): string {
  return kind === 'archived'
    ? `sessions-list-archived:${userId}`
    : `sessions-list-active:${userId}`;
}

export async function invalidateSessionsListCaches(userId?: string): Promise<void> {
  // withCache bypasses Redis in development — skip purge noise (legacy + scoped DELETEs)
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  let id = userId;
  if (!id) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    id = user?.id;
  }
  if (id) {
    await PowerCacheHelper.delete(sessionsListCacheKey('active', id));
    await PowerCacheHelper.delete(sessionsListCacheKey('archived', id));
  }
  // Purge legacy global keys so poisoned cross-user entries cannot linger
  await PowerCacheHelper.delete('sessions-list-active');
  await PowerCacheHelper.delete('sessions-list-archived');
  await PowerCacheHelper.delete('sessions-list');
}

export interface ParameterInfo {
  id: string;
  session_id: string;
  sweep_oar_length: number | null;
  sweep_oar_inboard: number | null;
  sweep_drive_start: number | null;
  sweep_recovery: number | null;
  scull_oar_length: number | null;
  scull_oar_inboard: number | null;
  scull_drive_start: number | null;
  scull_recovery: number | null;
  impeller_scaling: number | null;
  average_rating_over: number | null;
  created_at: string;
}

export interface SessionRosterEntry {
  id: string;
  sessionId: string;
  position: number;
  rosterId: string | null;
  seatId: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  createdAt: string | null;
}

export interface SessionRosterChange {
  rowerId: string | null;
  seatPosition: number;
  newRosterId: string | null;
  newSeatId: string | null;
  newDisplayName: string | null;
  newFirstName: string | null;
  newLastName: string | null;
  newPreferredName?: string | null; // New field for preferred name
  previousRosterId: string | null;
  previousSeatId: string | null;
  previousDisplayName: string | null;
  createNewProfile?: boolean; // New flag for Scenario 1
}

export interface SessionRosterUpdateResult {
  success: boolean;
  roster: SessionRosterEntry[];
}

// Type-safe transformation function for Supabase data
function transformToParameterInfo(data: { [x: string]: unknown }): ParameterInfo | null {
  // Validate required fields exist and have correct types
  if (!data || typeof data.id !== 'string' || typeof data.session_id !== 'string') {
    return null;
  }

  // Transform with proper type checking and defaults
  return {
    id: data.id,
    session_id: data.session_id,
    sweep_oar_length: typeof data.sweep_oar_length === 'number' ? data.sweep_oar_length : null,
    sweep_oar_inboard: typeof data.sweep_oar_inboard === 'number' ? data.sweep_oar_inboard : null,
    sweep_drive_start: typeof data.sweep_drive_start === 'number' ? data.sweep_drive_start : null,
    sweep_recovery: typeof data.sweep_recovery === 'number' ? data.sweep_recovery : null,
    scull_oar_length: typeof data.scull_oar_length === 'number' ? data.scull_oar_length : null,
    scull_oar_inboard: typeof data.scull_oar_inboard === 'number' ? data.scull_oar_inboard : null,
    scull_drive_start: typeof data.scull_drive_start === 'number' ? data.scull_drive_start : null,
    scull_recovery: typeof data.scull_recovery === 'number' ? data.scull_recovery : null,
    impeller_scaling: typeof data.impeller_scaling === 'number' ? data.impeller_scaling : null,
    average_rating_over:
      typeof data.average_rating_over === 'number' ? data.average_rating_over : null,
    created_at: typeof data.created_at === 'string' ? data.created_at : new Date().toISOString(),
  };
}

export const telemetryService = {
  // Resolve session ID to UUID for database queries
  async resolveSessionId(inputSessionId: string): Promise<string | null> {
    // Guard against null/undefined/whitespace and string placeholders
    const raw = (inputSessionId ?? '') as unknown as string;
    const v = typeof raw === 'string' ? raw.trim() : '';
    const vl = v.toLowerCase();
    if (!v || vl === 'null' || vl === 'undefined') {
      return null;
    }

    // First try direct UUID match (if input is already UUID)
    const directMatch = await supabase
      .from('telemetry_sessions')
      .select('id')
      .eq('id', v)
      .maybeSingle();

    if (directMatch.data) {
      return v; // Already a UUID
    }

    // Otherwise resolve text session_id to UUID
    const resolvedMatch = await supabase
      .from('telemetry_sessions')
      .select('id')
      .eq('session_id', v)
      .maybeSingle();

    const resolvedId = (resolvedMatch.data?.id as string) || null;
    return resolvedId;
  },

  // Get sessions (active only by default; archived sessions for recovery UI)
  async getSessions(options?: { archived?: boolean }): Promise<SessionInfo[]> {
    const archived = options?.archived ?? false;

    const {
      data: { user },
    } = await supabase.auth.getUser();
    const userId = user?.id;

    const fetchAndMap = async (): Promise<SessionInfo[]> => {
      let query = supabase
        .from('telemetry_sessions')
        .select('*, teams:team_id(name), boats:boat_id(name)')
        .order('session_date', { ascending: false })
        .gte('created_at', '1900-01-01');

      query = archived ? query.not('archived_at', 'is', null) : query.is('archived_at', null);

      const { data, error } = await query;

      if (error) throw error;

      const mappedSessions = (data || []).map((session: any) => ({
        id: session.id as string,
        sessionId: session.session_id as string,
        sessionName: session.session_name as string, // Add session name
        crewName: session.crew_name as string, // Add crew name
        sessionDate: session.session_date as string,
        startTime: session.start_time as string,
        location: session.location as string,
        boatType: session.boat_type as string,
        file_size: session.file_size as number | undefined,
        created_at: session.created_at as string,
        isDemo: session.is_demo === true,
        isPartial: session.is_partial === true,
        team_id: session.team_id as string | null | undefined,
        team_name: session.teams?.name as string | null | undefined,
        boat_id: session.boat_id as string | null | undefined,
        boat_name: session.boats?.name as string | null | undefined,
        coach_id: session.coach_id as string | undefined,
        archived_at: (session.archived_at as string | null | undefined) ?? null,
        archived_by: (session.archived_by as string | null | undefined) ?? null,
      }));

      const sessionIds = mappedSessions.map((session) => session.id).filter(Boolean);
      if (sessionIds.length === 0) {
        return mappedSessions;
      }

      const { data: rosterRows, error: rosterError } = await supabase
        .from('telemetry_rowers')
        .select('session_id, position, roster_id')
        .in('session_id', sessionIds);

      if (rosterError) throw rosterError;

      const rosterSummaryBySession = new Map<
        string,
        { roster_status: SessionInfo['roster_status']; roster_mapped: number; roster_total: number }
      >();

      (rosterRows || []).forEach((row: any) => {
        const sessionId = row.session_id as string | null;
        const position = row.position as number | null;

        if (!sessionId || typeof position !== 'number' || position < 1 || position > 8) {
          return;
        }

        const existing = rosterSummaryBySession.get(sessionId) ?? {
          roster_status: 'unmapped' as const,
          roster_mapped: 0,
          roster_total: 8,
        };

        if (row.roster_id) {
          existing.roster_mapped += 1;
        }

        rosterSummaryBySession.set(sessionId, existing);
      });

      rosterSummaryBySession.forEach((summary) => {
        if (summary.roster_mapped === 0) {
          summary.roster_status = 'unmapped';
        } else if (summary.roster_mapped >= summary.roster_total) {
          summary.roster_status = 'fully-mapped';
        } else {
          summary.roster_status = 'partially-mapped';
        }
      });

      return mappedSessions.map((session) => {
        const rosterSummary = rosterSummaryBySession.get(session.id);
        return {
          ...session,
          roster_status: rosterSummary?.roster_status ?? 'unmapped',
          roster_mapped: rosterSummary?.roster_mapped ?? 0,
          roster_total: rosterSummary?.roster_total ?? 8,
        };
      });
    };

    // Never use a global list cache — skip Redis when unauthenticated
    if (!userId) {
      return fetchAndMap();
    }

    const cacheKey = sessionsListCacheKey(archived ? 'archived' : 'active', userId);
    return PowerCacheHelper.withCache(cacheKey, fetchAndMap);
  },

  // Get stroke data for a specific rower
  async getStrokeData(sessionId: string, rowerPosition: number): Promise<StrokeData[]> {
    const resolvedId = await this.resolveSessionId(sessionId);

    if (!resolvedId) {
      return [];
    }

    const { data, error } = await supabase
      .from('telemetry_data')
      .select('gate_angle, gate_force_x, timestamp')
      .eq('session_id', resolvedId)
      .eq('rower_position', rowerPosition)
      .order('timestamp', { ascending: true });

    if (error) throw error;

    return (data || []).map((point: any) => ({
      angle: point.gate_angle as number,
      force: point.gate_force_x as number,
      timestamp: typeof point.timestamp === 'number' ? point.timestamp : undefined,
    }));
  },

  async getGateForceUniformSamples(
    sessionId: string,
    options?: { rowerPositions?: number[]; targetPoints?: number }
  ): Promise<GateForceSessionSamplerResult | null> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) {
      return null;
    }

    const targetPoints = options?.targetPoints ?? 1200;
    const { data, error } = await supabase.rpc('sample_session_uniform_v1', {
      p_session_id: resolvedId,
      p_rower_positions:
        options?.rowerPositions && options.rowerPositions.length > 0
          ? options.rowerPositions
          : null,
      p_target_points: targetPoints,
    });

    if (error) {
      throw error;
    }

    const rows = (data ?? []) as UniformSamplerRow[];

    if (rows.length === 0) {
      return null;
    }

    const series: Record<string, GateForceTimeSeries> = {};
    const bucketMs = Number(rows[0]?.bucket_ms ?? 0);

    rows.forEach((row) => {
      if (typeof row.rower_position !== 'number') return;
      const key = buildRowerKey(row.rower_position);
      series[key] = {
        ts: toFloat32Array(row.ts_ms),
        angle: toFloat32Array(row.angle_deg),
        force: toFloat32Array(row.force_kgf),
      };
    });

    if (Object.keys(series).length === 0) {
      return null;
    }

    return {
      series,
      meta: {
        bucketMs,
        targetPoints,
        source: SAMPLER_SOURCE,
      },
    };
  },

  async getGateForceMinMaxSamples(
    sessionId: string,
    options?: { rowerPositions?: number[]; bucketCount?: number }
  ): Promise<GateForceSessionSamplerResult | null> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) {
      return null;
    }

    const bucketCount = options?.bucketCount ?? 600;
    const { data, error } = await supabase.rpc('sample_session_minmax_v1', {
      p_session_id: resolvedId,
      p_rower_positions:
        options?.rowerPositions && options.rowerPositions.length > 0
          ? options.rowerPositions
          : null,
      p_bucket_count: bucketCount,
    });

    if (error) {
      throw error;
    }

    const rows = (data ?? []) as UniformSamplerRow[];

    if (rows.length === 0) {
      return null;
    }

    const series: Record<string, GateForceTimeSeries> = {};
    const bucketMs = Number(rows[0]?.bucket_ms ?? 0);

    rows.forEach((row) => {
      if (typeof row.rower_position !== 'number') return;
      const key = buildRowerKey(row.rower_position);
      series[key] = {
        ts: toFloat32Array(row.ts_ms),
        angle: toFloat32Array(row.angle_deg),
        force: toFloat32Array(row.force_kgf),
      };
    });

    if (Object.keys(series).length === 0) {
      return null;
    }

    return {
      series,
      meta: {
        bucketMs,
        targetPoints: bucketCount,
        source: SAMPLER_SOURCE,
      },
    };
  },

  async getGateForceStrokeSamples(
    sessionId: string,
    options?: { rowerPositions?: number[]; targetPoints?: number; minPointsPerStroke?: number }
  ): Promise<GateForceStrokeSamplerResult | null> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) {
      return null;
    }

    const targetPoints = options?.targetPoints ?? 180;
    const minPoints = options?.minPointsPerStroke ?? 20;

    // Use pagination to get all data, bypassing the 1000 row RPC limit
    let allRows: StrokeSamplerRow[] = [];
    let offset = 0;
    const limit = 1000; // Process in chunks of 1000

    while (true) {
      const { data, error } = await supabase.rpc('sample_strokes_resampled_v1_fast', {
        p_session_id: resolvedId,
        p_rower_positions:
          options?.rowerPositions && options.rowerPositions.length > 0
            ? options.rowerPositions
            : null,
        p_target_points: targetPoints,
        p_min_points_per_stroke: minPoints,
        p_offset: offset,
        p_limit: limit,
      });

      if (error) {
        throw error;
      }

      const rows = (data ?? []) as StrokeSamplerRow[];
      if (rows.length === 0) {
        break; // No more data
      }

      allRows = [...allRows, ...rows];

      if (rows.length < limit) {
        break; // Last chunk
      }

      offset += limit;
    }

    const rows = allRows;

    if (rows.length === 0) {
      return null;
    }

    const perRower: Map<number, GateForceStrokeSamplerRow> = new Map();

    rows.forEach((row) => {
      if (typeof row.rower_position !== 'number') return;
      const rowerPosition = row.rower_position as number;
      const strokeSeries: GateForceStrokeSeries = {
        strokeIndex: Number(row.stroke_index ?? 0),
        angle: toFloat32Array(row.angle_deg),
        force: toFloat32Array(row.force_kgf),
      };

      const existing = perRower.get(rowerPosition) ?? {
        strokes: [],
        skippedStrokes: 0,
        totalStrokes: 0,
      };

      existing.strokes.push(strokeSeries);
      existing.skippedStrokes = Number(row.skipped_strokes ?? existing.skippedStrokes ?? 0);
      existing.totalStrokes = Number(row.total_strokes ?? existing.totalStrokes ?? 0);

      perRower.set(rowerPosition, existing);
    });

    if (perRower.size === 0) {
      return null;
    }

    const series: Record<string, GateForceStrokeSamplerRow> = {};

    perRower.forEach((value, rowerPosition) => {
      const key = buildRowerKey(rowerPosition);
      series[key] = {
        strokes: value.strokes.sort((a, b) => a.strokeIndex - b.strokeIndex),
        skippedStrokes: value.skippedStrokes,
        totalStrokes: value.totalStrokes,
      };
    });

    return {
      series,
      meta: {
        targetPoints,
        source: SAMPLER_SOURCE,
      },
    };
  },

  async getGateForceMedianProfile(
    sessionId: string,
    options?: { rowerPositions?: number[]; targetPoints?: number; minPointsPerStroke?: number }
  ): Promise<GateForceProfileResult | null> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) {
      return null;
    }

    const targetPoints = options?.targetPoints ?? 180;
    const minPoints = options?.minPointsPerStroke ?? 20;

    const { data, error } = await supabase.rpc('profile_median_by_angle_v1_fast', {
      p_session_id: resolvedId,
      p_rower_positions:
        options?.rowerPositions && options.rowerPositions.length > 0
          ? options.rowerPositions
          : null,
      p_target_points: targetPoints,
      p_min_points_per_stroke: minPoints,
    });

    if (error) {
      throw error;
    }

    const rows = (data ?? []) as MedianProfileRow[];

    if (rows.length === 0) {
      return null;
    }

    const series: Record<string, GateForceProfileSeries> = {};

    rows.forEach((row) => {
      if (typeof row.rower_position !== 'number') return;
      const key = buildRowerKey(row.rower_position);
      series[key] = {
        angle: toFloat32Array(row.angle_deg),
        force: toFloat32Array(row.force_kgf),
      };
    });

    if (Object.keys(series).length === 0) {
      return null;
    }

    return {
      series,
      meta: {
        targetPoints,
        source: SAMPLER_SOURCE,
      },
    };
  },

  // getStrokeGeometry method removed - stroke_geometry table was created based on false assumptions
  // Arc metrics are now calculated client-side from telemetry_data via useArcMetrics hook

  // Get decimated stroke data for visualization (performance optimized)
  async getDecimatedStrokeData(
    sessionId: string,
    rowerPosition: number,
    targetPoints: number = 1000
  ): Promise<StrokeData[]> {
    const resolvedId = await this.resolveSessionId(sessionId);

    if (!resolvedId) {
      return [];
    }

    const { data, error } = await supabase
      .from('telemetry_data')
      .select('gate_angle, gate_force_x, timestamp')
      .eq('session_id', resolvedId)
      .eq('rower_position', rowerPosition)
      .order('timestamp', { ascending: true })
      .range(0, 49999); // Get up to 50,000 records (0-49999 inclusive)

    if (error) throw error;

    if (!data || data.length === 0) {
      return [];
    }

    // Use decimation with performance monitoring
    const { result } = measureDecimation(
      () =>
        decimateData(
          data.map((point: any) => ({
            x: point.gate_angle as number,
            y: point.gate_force_x as number,
            timestamp: point.timestamp,
          })),
          targetPoints
        ),
      data.length,
      targetPoints
    );

    return result.map((point) => ({
      angle: point.x,
      force: point.y,
    }));
  },

  // Convert raw stroke data into proper stroke cycles for gate force signature
  processStrokeDataForSignature(rawData: StrokeData[], maxStrokes: number = 3): StrokeData[] {
    return strokeAnalysis.processStrokeDataForSignature(rawData, maxStrokes);
  },

  // Get complete stroke cycle (catch to finish) for a time range
  async getStrokeCycle(
    sessionId: string,
    rowerPosition: number,
    startTime: number,
    endTime: number
  ): Promise<StrokeData[]> {
    const resolvedId = await this.resolveSessionId(sessionId);

    if (!resolvedId) {
      return [];
    }

    const { data, error } = await supabase
      .from('telemetry_data')
      .select('gate_angle, gate_force_x')
      .eq('session_id', resolvedId)
      .eq('rower_position', rowerPosition)
      .gte('timestamp', startTime)
      .lte('timestamp', endTime)
      .order('timestamp', { ascending: true });

    if (error) throw error;

    return (data || []).map((point: any) => ({
      angle: point.gate_angle as number,
      force: point.gate_force_x as number,
    }));
  },

  // Get a sample stroke cycle for quick display
  async getSampleStrokeCycle(sessionId: string, rowerPosition: number): Promise<StrokeData[]> {
    const resolvedId = await this.resolveSessionId(sessionId);

    if (!resolvedId) {
      return [];
    }

    // First, get a sample of data to find a complete stroke
    const { data: sampleData, error: sampleError } = await supabase
      .from('telemetry_data')
      .select('timestamp, gate_angle')
      .eq('session_id', resolvedId)
      .eq('rower_position', rowerPosition)
      .gte('timestamp', 4200000) // Start from a known point
      .lte('timestamp', 4201000) // About 1 second of data
      .order('timestamp', { ascending: true });

    if (sampleError) throw sampleError;

    // Find a complete stroke cycle (catch to finish)
    let catchIndex = -1;
    let finishIndex = -1;

    for (let i = 0; i < sampleData.length - 1; i++) {
      // Look for catch (large negative angle)
      const gateAngle = sampleData[i].gate_angle;
      if (typeof gateAngle === 'number' && gateAngle < -50 && catchIndex === -1) {
        catchIndex = i;
      }
      // Look for finish (positive angle after catch)
      if (catchIndex !== -1 && typeof gateAngle === 'number' && gateAngle > 20) {
        finishIndex = i;
        break;
      }
    }

    if (catchIndex === -1 || finishIndex === -1) {
      // If no complete stroke found, return all data
      return this.getStrokeCycle(
        sessionId,
        rowerPosition,
        sampleData[0].timestamp,
        sampleData[sampleData.length - 1].timestamp
      );
    }

    // Get the complete stroke data
    return this.getStrokeCycle(
      sessionId,
      rowerPosition,
      sampleData[catchIndex].timestamp,
      sampleData[finishIndex].timestamp
    );
  },

  // Get power output data for all rowers (for TelemetryChart)
  async getPowerOutputData(sessionId: string): Promise<any[]> {
    return PowerCacheHelper.withCache(`power-output-${sessionId}`, async () => {
      const resolvedId = await this.resolveSessionId(sessionId);

      if (!resolvedId) {
        return [];
      }

      // Get intervals from Peach file intervals section
      const { data: intervals, error: intervalsError } = await supabase
        .from('telemetry_intervals')
        .select('*')
        .eq('session_id', resolvedId)
        .order('interval_number', { ascending: true });

      if (intervalsError) throw intervalsError;

      // If no intervals found, create synthetic intervals from telemetry data
      let useIntervals = intervals || [];
      if (useIntervals.length === 0) {
        // Get time range from telemetry data
        const { data: timeRange } = await supabase
          .from('telemetry_data')
          .select('timestamp')
          .eq('session_id', resolvedId)
          .order('timestamp', { ascending: true })
          .limit(1);

        const { data: endTimeData } = await supabase
          .from('telemetry_data')
          .select('timestamp')
          .eq('session_id', resolvedId)
          .order('timestamp', { ascending: false })
          .limit(1);

        if (timeRange && timeRange[0] && endTimeData && endTimeData[0]) {
          // Generate multiple intervals based on session duration
          const startTime = Number(timeRange[0].timestamp);
          const endTime = Number(endTimeData[0].timestamp);
          const totalDuration = endTime - startTime;
          let intervalDuration: number;

          // Adaptive interval sizing
          if (totalDuration < 60000) {
            // < 1 minute
            intervalDuration = 10000; // 10 second intervals
          } else if (totalDuration < 300000) {
            // < 5 minutes
            intervalDuration = 30000; // 30 second intervals
          } else if (totalDuration < 1800000) {
            // < 30 minutes
            intervalDuration = 60000; // 1 minute intervals
          } else {
            intervalDuration = 120000; // 2 minute intervals
          }

          const numIntervals = Math.ceil(totalDuration / intervalDuration);
          useIntervals = [];

          for (let i = 0; i < numIntervals; i++) {
            const intervalStart = startTime + i * intervalDuration;
            const intervalEnd = Math.min(intervalStart + intervalDuration, endTime);

            useIntervals.push({
              interval_number: i + 1,
              start_time: intervalStart,
              end_time: intervalEnd,
              duration_ms: intervalEnd - intervalStart,
            });
          }
        }
      }

      // Get telemetry data for power calculation
      const { data: telemetryData, error: telemetryError } = await supabase
        .from('telemetry_data')
        .select('*')
        .eq('session_id', resolvedId)
        .order('timestamp', { ascending: true });

      if (telemetryError) throw telemetryError;
      if (!telemetryData || telemetryData.length === 0) {
        return [];
      }

      // Use extracted dataProcessing module for interval processing
      const chartData = await dataProcessing.processIntervalData(
        sessionId,
        useIntervals,
        telemetryData
      );

      return chartData;
    });
  },

  // Get pre-calculated SwivelPower data from Aperiodic 0x800A section
  async getSwivelPowerTimeSeries(sessionId: string): Promise<{
    success: boolean;
    data: Array<{ time: number; [rower: string]: number }>;
    strokeCount?: number;
    isSwivelPowerData?: boolean;
  }> {
    return PowerCacheHelper.withCache(`swivel-power-time-series-${sessionId}`, async () => {
      const resolvedId = await this.resolveSessionId(sessionId);
      if (!resolvedId) {
        return { success: false, data: [] };
      }

      // Get stroke metrics data (0x800A) from database

      const { data: strokeMetrics, error: strokeError } = await supabase
        .from('telemetry_stroke_metrics')
        .select(
          'timestamp, rower_1_power, rower_2_power, rower_3_power, rower_4_power, rower_5_power, rower_6_power, rower_7_power, rower_8_power'
        )
        .eq('session_id', resolvedId)
        .order('timestamp', { ascending: true });

      // Double-check what's actually in the database
      const { data: _allStrokeMetrics, error: _allError } = await supabase
        .from('telemetry_stroke_metrics')
        .select('session_id')
        .eq('session_id', resolvedId);

      if (strokeError) throw strokeError;
      if (!strokeMetrics || strokeMetrics.length === 0) {
        // Fall back to physics-based calculation if no 0x800A data
        const fallbackResult = await this.getPowerOutputTimeSeries(sessionId);
        return {
          ...fallbackResult,
          isSwivelPowerData: false,
        };
      }

      // Get start time for relative time calculation
      const startTime = Number(strokeMetrics[0].timestamp);

      // Transform stroke metrics to time series format
      const timeSeries = strokeMetrics.map((metric) => {
        const relativeTime = (Number(metric.timestamp) - startTime) / 1000; // Convert to seconds
        const dataPoint: { time: number; [rower: string]: number } = { time: relativeTime };

        // Add power values for each rower
        for (let i = 1; i <= 8; i++) {
          const powerKey = `rower_${i}_power` as keyof typeof metric;
          const power = metric[powerKey];
          if (power !== null && power !== undefined) {
            dataPoint[`Rower ${i}`] = Number(power);
          }
        }

        return dataPoint;
      });

      return {
        success: true,
        data: timeSeries,
        strokeCount: strokeMetrics.length,
        isSwivelPowerData: true,
      };
    });
  },

  // New: get per-stroke unified series (crew) from physics_power_stroke_cal_v1
  async getUnifiedPerStrokeSeries(sessionId: string): Promise<number[]> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) return [];
    const { data, error } = await supabase
      .from('physics_power_stroke_cal_v1')
      .select('p_unified_w')
      .eq('session_id', resolvedId)
      .order('stroke_index', { ascending: true });
    if (error) return [];
    return (data || [])
      .map((r: any) => Number(r.p_unified_w))
      .filter((v) => typeof v === 'number' && isFinite(v));
  },

  // New: per-stroke unified series with timestamps (best-effort)
  // If unified table lacks timestamps, align by index with telemetry_stroke_metrics timestamps.
  async getUnifiedPerStrokeSeriesWithTimes(
    sessionId: string
  ): Promise<{ values: number[]; times: number[] }> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) return { values: [], times: [] };

    // Fetch unified values
    const { data: unifiedRows, error: unifiedErr } = await supabase
      .from('physics_power_stroke_cal_v1')
      .select('p_unified_w')
      .eq('session_id', resolvedId)
      .order('stroke_index', { ascending: true });
    if (unifiedErr || !unifiedRows) return { values: [], times: [] };
    const unifiedValues: number[] = (unifiedRows || [])
      .map((r: any) => Number(r.p_unified_w))
      .filter((v) => typeof v === 'number' && isFinite(v));

    // Fetch timestamps from telemetry_stroke_metrics (one per stroke)
    const { data: tsRows } = await supabase
      .from('telemetry_stroke_metrics')
      .select('timestamp')
      .eq('session_id', resolvedId)
      .order('timestamp', { ascending: true });
    let times: number[] = (tsRows || [])
      .map((r: any) => Number(r.timestamp))
      .filter((t) => typeof t === 'number' && isFinite(t) && t > 0);

    // Align by shortest length
    const len = Math.min(unifiedValues.length, times.length);
    if (len === 0) return { values: unifiedValues, times: [] };

    // Normalize to elapsed time (ms) so charts start at 0:00
    const slicedTimes = times.slice(0, len);
    const t0 = slicedTimes[0] ?? 0;
    const normalizedTimes = slicedTimes.map((t) => t - t0);
    return { values: unifiedValues.slice(0, len), times: normalizedTimes };
  },

  // New: per-stroke crew series from 0x800A by averaging rower_N_power per stroke
  async getSwivelPerStrokeCrewSeries(sessionId: string): Promise<number[]> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) return [];
    const { data, error } = await supabase
      .from('telemetry_stroke_metrics')
      .select(
        'rower_1_power,rower_2_power,rower_3_power,rower_4_power,rower_5_power,rower_6_power,rower_7_power,rower_8_power'
      )
      .eq('session_id', resolvedId)
      .order('timestamp', { ascending: true });
    if (error) return [];
    return (data || []).map((row: any) => {
      const vals = [1, 2, 3, 4, 5, 6, 7, 8]
        .map((i) => Number(row[`rower_${i}_power`]))
        .filter((v) => typeof v === 'number' && isFinite(v) && v > 0);
      if (!vals.length) return 0;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    });
  },

  // New: per-stroke crew series (Swivel) with timestamps
  async getSwivelPerStrokeCrewSeriesWithTimes(
    sessionId: string
  ): Promise<{ values: number[]; times: number[] }> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) return { values: [], times: [] };
    const { data, error } = await supabase
      .from('telemetry_stroke_metrics')
      .select(
        'timestamp,rower_1_power,rower_2_power,rower_3_power,rower_4_power,rower_5_power,rower_6_power,rower_7_power,rower_8_power'
      )
      .eq('session_id', resolvedId)
      .order('timestamp', { ascending: true });
    if (error || !data) return { values: [], times: [] };
    const times: number[] = [];
    const values: number[] = [];
    (data || []).forEach((row: any) => {
      const t = Number(row.timestamp);
      if (!isFinite(t) || t <= 0) return; // skip header row or invalid
      const vals = [1, 2, 3, 4, 5, 6, 7, 8]
        .map((i) => Number(row[`rower_${i}_power`]))
        .filter((v) => typeof v === 'number' && isFinite(v) && v > 0);
      if (vals.length) {
        times.push(t);
        values.push(vals.reduce((a, b) => a + b, 0) / vals.length);
      }
    });
    // Normalize to elapsed time (ms)
    const t0 = times[0] ?? 0;
    const normalizedTimes = times.map((t) => t - t0);
    return { values, times: normalizedTimes };
  },

  // New: fetch latest calibration info (alpha, r2, method)
  async getCalibrationInfo(sessionId: string): Promise<{
    alpha: number | null;
    r2: number | null;
    method: 'whole' | 'drive' | null;
  } | null> {
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) return null;
    const { data, error } = await supabase
      .from('calibration')
      .select('alpha, r2, method')
      .eq('session_id', resolvedId)
      .eq('model', 'physics_power_cal_v1')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    return {
      alpha: typeof (data as any).alpha === 'number' ? (data as any).alpha : null,
      r2: typeof (data as any).r2 === 'number' ? (data as any).r2 : null,
      method: (data as any).method ?? null,
    };
  },

  // Get power output data as time-series (for continuous graphs like HTML file)
  async getPowerOutputTimeSeries(sessionId: string): Promise<{
    success: boolean;
    data: Array<{ time: number; [rower: string]: number }>;
    strokeCount?: number;
    strokeBoundaries?: Array<{ start: number; end: number | null }>;
    workBreakdown?: {
      [rower: string]: { drivePhaseWork: number; recoveryPhaseWork: number; totalWork: number };
    };
    strokeAverages?: {
      [rower: string]: Array<{ duration: number; totalWork: number; averagePower: number }>;
    };
    reason?: string;
    resolvedId?: string;
    totalRows?: number;
    rawCount?: number;
    error?: any;
  }> {
    const leverMode = getLeverMode();
    return PowerCacheHelper.withCache(`power-time-series-${sessionId}-${leverMode}`, async () => {
      // Log the input
      DEBUG.log(
        'TELEMETRY',
        '[DATA_TRACE] getPowerOutputTimeSeries called with sessionId:',
        sessionId
      );
      const resolvedId = await this.resolveSessionId(sessionId);

      if (!resolvedId) {
        return { success: false, data: [], reason: 'uuid-resolution-failed' };
      }

      // HEAD count query to verify data exists before processing
      const { count: totalRows, error: _headError } = await supabase
        .from('telemetry_data')
        .select('timestamp', { count: 'exact', head: true })
        .eq('session_id', resolvedId);

      if (totalRows === 0 || totalRows === null) {
        return { success: false, data: [], reason: 'no-periodic-for-uuid', resolvedId, totalRows };
      }

      // Get all telemetry data using totalRows from HEAD count
      const total = totalRows ?? 0;
      const baseQuery = () =>
        supabase
          .from('telemetry_data')
          .select('timestamp, rower_position, gate_force_x, gate_angle_velocity, gate_angle')
          .eq('session_id', resolvedId)
          .order('timestamp', { ascending: true });

      // Try single range query first
      let telemetryData: any[] = [];
      let telemetryError = null;
      let _status = 200;
      let _count = null;

      if (total > 0) {
        const { data, error } = await baseQuery().range(0, Math.max(0, total - 1));
        if (error) {
          telemetryError = error;
        } else {
          telemetryData = data ?? [];
        }

        // Fallback: paginate if we didn't get all rows
        if (!telemetryError && telemetryData.length < total) {
          telemetryData = [];
          const CHUNK = 1000;

          for (let _from = 0; _from < total; _from += CHUNK) {
            const _to = Math.min(_from + CHUNK - 1, total - 1);
            const { data: page, error: pageError } = await baseQuery().range(_from, _to);

            if (pageError) {
              telemetryError = pageError;
              break;
            }

            if (page?.length) {
              telemetryData.push(...page);
            }
          }
        }
      } else {
        // Fallback to original query if HEAD count failed
        const result = await baseQuery();
        telemetryData = result.data ?? [];
        telemetryError = result.error;
        _status = result.status;
        _count = result.count;
      }

      if (telemetryError) {
        return {
          success: false,
          data: [],
          reason: 'telemetry-fetch-error',
          error: telemetryError,
          resolvedId,
          totalRows,
        };
      }

      if (!telemetryData || telemetryData.length === 0) {
        DEBUG.log(
          'TELEMETRY',
          '🔍 GLYYD_DATA_TRACE_006: No telemetry data found in database - returning empty'
        );
        DEBUG.log(
          'TELEMETRY',
          '[DATA_TRACE][DEBUG] Query returned empty result despite HEAD count:',
          { totalRows }
        );
        return { success: false, data: [], reason: 'telemetry-query-empty', resolvedId, totalRows };
      }

      // Get start time for relative time calculation
      const startTime = Number(telemetryData[0].timestamp);
      DEBUG.log(
        'TELEMETRY',
        '🔍 GLYYD_DATA_TRACE_007: Using telemetry data with startTime:',
        startTime
      );

      // Log physics input before processing
      console.log('[PHYSICS][DEBUG] input size', {
        totalRowsHead: totalRows,
        fetched: telemetryData.length,
        t0: telemetryData[0]?.timestamp,
        tN: telemetryData[telemetryData.length - 1]?.timestamp,
      });

      // Get session parameters for physics-based calculation
      // Belt-and-suspenders: ensure non-null parameters even if DB read fails
      const fetchedParams = await this.getParameterInfo(resolvedId);
      const parameterInfo = fetchedParams || {
        id: 'inline-defaults',
        session_id: resolvedId,
        sweep_oar_length: 376,
        sweep_oar_inboard: 115,
        sweep_drive_start: 30,
        sweep_recovery: null,
        scull_oar_length: null,
        scull_oar_inboard: null,
        scull_drive_start: null,
        scull_recovery: null,
        impeller_scaling: null,
        average_rating_over: null,
        created_at: new Date().toISOString(),
      };
      const result = powerCalculations.calculateStrokeIntegratedPower(
        telemetryData,
        startTime,
        parameterInfo
      );

      return {
        success: true,
        data: result.timeSeries,
        strokeCount: result.strokeCount,
        strokeBoundaries: result.strokeBoundaries,
        workBreakdown: result.workBreakdown,
        strokeAverages: result.strokeAverages,
        totalRows, // HEAD count from before processing
        rawCount: telemetryData.length, // actual rows fetched and processed
      };
    });
  },

  // Get power output data as time-series using RPC sampling (optimized for large datasets)
  async getSampledPowerTimeSeries(
    sessionId: string,
    targetPoints: number = 1200,
    rowerPositions?: number[]
  ): Promise<{
    success: boolean;
    data: Array<{ time: number; [rower: string]: number }>;
    totalRows?: number;
    reason?: string;
    error?: any;
  }> {
    const leverMode = getLeverMode();
    const cacheKey = `sampled-power-time-series-${sessionId}-${leverMode}-${targetPoints}-${rowerPositions?.join(',') || 'all'}`;
    return PowerCacheHelper.withCache(cacheKey, async () => {
      const resolvedId = await this.resolveSessionId(sessionId);
      if (!resolvedId) {
        return { success: false, data: [], reason: 'uuid-resolution-failed' };
      }

      try {
        // Use RPC for server-side sampling - single database call
        // Pass rowerPositions to filter server-side (compliance for rowers)
        const { data: sampledData, error } = await supabase.rpc('sample_session_uniform_v1', {
          p_session_id: resolvedId,
          p_rower_positions: rowerPositions && rowerPositions.length > 0 ? rowerPositions : null,
          p_target_points: targetPoints,
        });

        if (error) {
          console.error('RPC Error Details:', error);
          return { success: false, data: [], reason: 'rpc-error', error };
        }

        if (!sampledData || !Array.isArray(sampledData) || sampledData.length === 0) {
          return { success: false, data: [], reason: 'no-sampled-data' };
        }

        // Transform RPC result to power time series format
        const powerData = await this.transformSampledDataToPowerSeries(sampledData, sessionId);

        return {
          success: true,
          data: powerData,
          totalRows: sampledData.length,
        };
      } catch (error) {
        return { success: false, data: [], reason: 'rpc-exception', error };
      }
    });
  },

  // Helper method to transform RPC data to power time series format
  async transformSampledDataToPowerSeries(
    sampledData: any[],
    sessionId: string
  ): Promise<Array<{ time: number; [rower: string]: number }>> {
    // Get parameter info for proper physics calculations (including leverage mode)
    const resolvedId = await this.resolveSessionId(sessionId);
    if (!resolvedId) return [];

    const parameterInfo = await this.getParameterInfo(resolvedId);
    if (!parameterInfo) return [];

    // Convert sampled data to telemetry format for powerCalculations
    const telemetryData: any[] = [];

    sampledData.forEach((row) => {
      const { rower_position, ts_ms, force_kgf, angle_deg, angular_vel_deg } = row;

      if (
        !ts_ms ||
        !Array.isArray(ts_ms) ||
        !force_kgf ||
        !Array.isArray(force_kgf) ||
        !angular_vel_deg ||
        !Array.isArray(angular_vel_deg)
      ) {
        return;
      }

      // Process each timestamp in the arrays
      for (let i = 0; i < ts_ms.length; i++) {
        const timestamp = Number(ts_ms[i]);
        const force = Number(force_kgf[i]);
        const angle = Number(angle_deg[i]) || 0;
        const angularVel = Number(angular_vel_deg[i]) || 0;

        if (isNaN(timestamp) || isNaN(force)) continue;

        telemetryData.push({
          timestamp,
          rower_position,
          gate_force_x: force,
          gate_angle: angle,
          gate_angle_velocity: angularVel,
        });
      }
    });

    if (telemetryData.length === 0) return [];

    // Use the same power calculation as the original method
    const startTime = Math.min(...telemetryData.map((d) => d.timestamp));
    const result = powerCalculations.calculateStrokeIntegratedPower(
      telemetryData,
      startTime,
      parameterInfo
    );

    return result.timeSeries;
  },

  // Get average power for full session (not intervals)
  async getFullSessionAveragePower(sessionId: string): Promise<{
    success: boolean;
    data: {
      averagePowerByRower: { [rower: number]: number };
      overallAverage: number;
      crewAverage: number;
      sessionDuration: number;
      dataPointCount: number;
      rowerPowers: { [rower: string]: number };
      strokeCount: number;
      individualStrokes: { [rower: string]: Array<any> };
      powerRange: { min: number; max: number };
      peakPower: number;
      consistency: number;
      enhancedRowerData: Array<{
        id: number;
        power: number;
        min: number;
        max: number;
        p25: number;
        p75: number;
        peak: number;
        consistency: number;
        powerDistribution: Array<{
          start: number;
          end: number;
          count: number;
          frequency: number;
        }>;
      }>;
      seatIdMapping: { [position: number]: string } | null;
    };
    overallAverage: number;
    sessionDuration: number;
    dataPointCount: number;
  }> {
    const resolvedId = await this.resolveSessionId(sessionId);
    const _leverMode = getLeverMode();

    if (!resolvedId) {
      console.error('❌ Session ID resolution failed');
      return {
        success: false,
        data: {
          averagePowerByRower: {},
          overallAverage: 0,
          crewAverage: 0, // Alias for OverviewMetricCards component
          sessionDuration: 0,
          dataPointCount: 0,
          rowerPowers: {},
          strokeCount: 0,
          individualStrokes: {},
          powerRange: { min: 0, max: 0 },
          peakPower: 0,
          consistency: 0,
          enhancedRowerData: [] as Array<{
            id: number;
            power: number;
            min: number;
            max: number;
            p25: number;
            p75: number;
            peak: number;
            consistency: number;
            powerDistribution: Array<{
              start: number;
              end: number;
              count: number;
              frequency: number;
            }>;
          }>,
          seatIdMapping: null,
        },
        overallAverage: 0,
        sessionDuration: 0,
        dataPointCount: 0,
      };
    }

    const { data, error } = await supabase
      .from('telemetry_data')
      .select('rower_position, gate_force_x, gate_angle_velocity, gate_angle, timestamp')
      .eq('session_id', resolvedId)
      .order('timestamp', { ascending: true });

    if (error) throw error;

    if (!data || data.length === 0) {
      return {
        success: true,
        data: {
          averagePowerByRower: {},
          overallAverage: 0,
          crewAverage: 0, // Alias for OverviewMetricCards component
          sessionDuration: 0,
          dataPointCount: 0,
          rowerPowers: {},
          strokeCount: 0,
          individualStrokes: {}, // Component doesn't use this
          powerRange: { min: 0, max: 0 },
          peakPower: 0,
          consistency: 0,
          enhancedRowerData: [] as Array<{
            id: number;
            power: number;
            min: number;
            max: number;
            p25: number;
            p75: number;
            peak: number;
            consistency: number;
            powerDistribution: Array<{
              start: number;
              end: number;
              count: number;
              frequency: number;
            }>;
          }>,
          seatIdMapping: null,
        },
        overallAverage: 0,
        sessionDuration: 0,
        dataPointCount: 0,
      };
    }

    /**
     * CRITICAL POWER CALCULATION FORMULAS - PHYSICS-BASED ROWING POWER
     *
     * ROWING PHYSICS FOUNDATION:
     * Power represents the rate of energy transfer from rower to water through the oar.
     * The fundamental physics equation: Power = Force × Velocity
     *
     * OAR LEVERAGE SYSTEM:
     * - Gate force sensor measures force at the oarlock (fulcrum point)
     * - Rower applies force at the handle (inboard section)
     * - Water resistance acts on the blade (outboard section)
     * - Lever ratio amplifies/reduces forces and velocities
     *
     * SENSOR DATA INTERPRETATION:
     * - gate_force_x: Force measured at oarlock in kilogram-force (kgf)
     * - gate_angle_velocity: Angular velocity of oar in degrees/second
     * - These raw measurements must be converted to handle forces/speeds
     *
     * CRITICAL CONVERSION FORMULAS:
     * 1. Force Scaling: Handle Force = Gate Force × (Outboard / Total Length)
     *    - Lever physics: force inversely proportional to arm length
     *    - Rower feels amplified force due to shorter inboard lever
     *
     * 2. Velocity Scaling: Handle Speed = Inboard × Angular Velocity
     *    - Linear velocity = radius × angular velocity
     *    - Rower's handle moves slower than blade tip (shorter radius)
     *
     * 3. Unit Conversions:
     *    - kgf → Newtons: multiply by 9.80665 (standard gravity)
     *    - degrees/sec → radians/sec: multiply by π/180
     *    - centimeters → meters: divide by 100
     *
     * PARAMETER SOURCES:
     * - Oar dimensions from session parameters or fallback defaults
     * - Sweep oar standard: ~376cm total, ~115cm inboard (varies by boat class)
     * - Scull oar different ratios (handled elsewhere in code)
     *
     * PHYSICS VALIDATION:
     * - Typical elite rower: 300-500 watts average power
     * - Power should correlate with stroke rate and force
     * - Negative power indicates sensor/calculation errors
     *
     * LIMITATIONS & ASSUMPTIONS:
     * - Assumes rigid oar (no flex modeling)
     * - Simplified angle dependency (full formula includes cos(θ) terms)
     * - No water resistance modeling (force measured, not calculated)
     * - Ignores oar slip angle and blade efficiency
     */

    // Get parameter info for physics calculations
    const parameterInfo = await this.getParameterInfo(resolvedId);

    // Use extracted powerCalculations module for pure calculation
    const powerResult = powerCalculations.calculateFullSessionAveragePower(data, parameterInfo);

    const sessionDuration =
      data.length > 0
        ? ((data[data.length - 1] as any).timestamp - (data[0] as any).timestamp) / (1000 * 60)
        : 0;

    // --- PATCH: Use max individual rower peak for peakPower ---
    let individualPeakPower = 0;
    if (powerResult.enhancedRowerData && powerResult.enhancedRowerData.length > 0) {
      individualPeakPower = Math.max(...powerResult.enhancedRowerData.map((r) => r.peak || 0));
    }
    // ---------------------------------------------------------

    DEBUG.log('POWER', 'Calculated averages:', powerResult.averagePowerByRower);
    DEBUG.log('POWER', 'Overall average:', powerResult.overallAverage);
    DEBUG.log('POWER', 'Power range:', powerResult.powerRange);
    DEBUG.log('POWER', 'Peak power (individual max):', individualPeakPower);
    DEBUG.log('POWER', 'Consistency:', powerResult.consistency);
    DEBUG.log('POWER', 'Enhanced rower data count:', powerResult.enhancedRowerData.length);
    DEBUG.log('POWER', 'Average Power Calculation pipeline completed');

    // Seat ID mapping for this session
    const seatIdMapping = await this.getSeatIdMapping(sessionId);

    return {
      success: true,
      data: {
        averagePowerByRower: powerResult.averagePowerByRower,
        overallAverage: powerResult.overallAverage,
        crewAverage: powerResult.overallAverage, // Alias for OverviewMetricCards component
        sessionDuration,
        dataPointCount: data.length,
        rowerPowers: {}, // Component doesn't use this
        strokeCount: 0, // Component doesn't use this
        individualStrokes: {}, // Component doesn't use this
        powerRange: powerResult.powerRange,
        peakPower: individualPeakPower, // PATCHED: use max individual rower peak
        consistency: powerResult.consistency,
        enhancedRowerData: powerResult.enhancedRowerData,
        seatIdMapping: seatIdMapping,
      },
      overallAverage: powerResult.overallAverage,
      sessionDuration,
      dataPointCount: data.length,
    };
  },

  // Get average power from 0x800A data (pre-calculated SwivelPower)
  async getSwivelPowerAveragePower(sessionId: string): Promise<{
    success: boolean;
    data: {
      averagePowerByRower: { [rower: number]: number };
      overallAverage: number;
      crewAverage: number;
      sessionDuration: number;
      dataPointCount: number;
      rowerPowers: { [rower: string]: number };
      strokeCount: number;
      individualStrokes: { [rower: string]: Array<any> };
      powerRange: { min: number; max: number };
      peakPower: number;
      consistency: number;
      enhancedRowerData: Array<{
        id: number;
        power: number;
        min: number;
        max: number;
        p25: number;
        p75: number;
        peak: number;
        consistency: number;
        powerDistribution: Array<{
          start: number;
          end: number;
          count: number;
          frequency: number;
        }>;
      }>;
      seatIdMapping?: { [position: number]: string } | null;
    };
    overallAverage: number;
    sessionDuration: number;
    dataPointCount: number;
  }> {
    DEBUG.log(
      'TELEMETRY',
      '🔍 SWIVEL_AVG_DEBUG: Starting getSwivelPowerAveragePower for sessionId:',
      sessionId
    );
    const resolvedId = await this.resolveSessionId(sessionId);

    if (!resolvedId) {
      console.error('❌ Session ID resolution failed');
      return {
        success: false,
        data: {
          averagePowerByRower: {},
          overallAverage: 0,
          crewAverage: 0,
          sessionDuration: 0,
          dataPointCount: 0,
          rowerPowers: {},
          strokeCount: 0,
          individualStrokes: {},
          powerRange: { min: 0, max: 0 },
          peakPower: 0,
          consistency: 0,
          enhancedRowerData: [],
        },
        overallAverage: 0,
        sessionDuration: 0,
        dataPointCount: 0,
      };
    }

    // Get 0x800A data from telemetry_stroke_metrics
    const { data, error } = await supabase
      .from('telemetry_stroke_metrics')
      .select('*')
      .eq('session_id', resolvedId)
      .order('timestamp', { ascending: true });

    if (error) throw error;

    if (!data || data.length === 0) {
      // No SwivelPower data available - Performance Metrics only works with Aperiodic data
      // Don't fall back to physics calculation from Periodic data
      return {
        success: false,
        data: {
          averagePowerByRower: {},
          overallAverage: 0,
          crewAverage: 0,
          sessionDuration: 0,
          dataPointCount: 0,
          rowerPowers: {},
          strokeCount: 0,
          individualStrokes: {},
          powerRange: { min: 0, max: 0 },
          peakPower: 0,
          consistency: 0,
          enhancedRowerData: [],
        },
        overallAverage: 0,
        sessionDuration: 0,
        dataPointCount: 0,
      };
    }

    // Extract power values for each rower from 0x800A data
    const rowerPowers: { [rower: number]: number[] } = {};
    let allPowers: number[] = [];
    let sessionStartTime = Infinity;
    let sessionEndTime = -Infinity;

    data.forEach((metric: any) => {
      // Skip header row (timestamp 0)
      if (metric.timestamp === 0) return;

      // Track session duration
      sessionStartTime = Math.min(sessionStartTime, metric.timestamp);
      sessionEndTime = Math.max(sessionEndTime, metric.timestamp);

      // Extract power values for each rower (rower_1_power through rower_8_power)
      for (let rower = 1; rower <= 8; rower++) {
        const powerKey = `rower_${rower}_power`;
        const powerValue = metric[powerKey];

        if (powerValue !== null && powerValue !== undefined && powerValue > 0) {
          if (!rowerPowers[rower]) {
            rowerPowers[rower] = [];
          }
          rowerPowers[rower].push(powerValue);
          allPowers.push(powerValue);
        }
      }
    });

    if (allPowers.length === 0) {
      // No valid power data in 0x800A - Performance Metrics only works with Aperiodic data
      // Don't fall back to physics calculation from Periodic data
      return {
        success: false,
        data: {
          averagePowerByRower: {},
          overallAverage: 0,
          crewAverage: 0,
          sessionDuration: 0,
          dataPointCount: 0,
          rowerPowers: {},
          strokeCount: 0,
          individualStrokes: {},
          powerRange: { min: 0, max: 0 },
          peakPower: 0,
          consistency: 0,
          enhancedRowerData: [],
        },
        overallAverage: 0,
        sessionDuration: 0,
        dataPointCount: 0,
      };
    }

    // Calculate averages and statistics
    const averagePowerByRower: { [rower: number]: number } = {};
    let totalPowerSum = 0;
    let rowerCount = 0;

    Object.entries(rowerPowers).forEach(([rower, powers]) => {
      if (powers.length > 0) {
        const avg = powers.reduce((a, b) => a + b, 0) / powers.length;
        averagePowerByRower[Number(rower)] = Math.round(avg);
        totalPowerSum += avg;
        rowerCount++;
      }
    });

    const overallAverage = rowerCount > 0 ? Math.round(totalPowerSum / rowerCount) : 0;

    // Calculate power range and peak power from 0x800A data
    const powerRange = {
      min: Math.min(...allPowers),
      max: Math.max(...allPowers),
    };

    const peakPower = powerRange.max;

    // Calculate consistency (standard deviation as percentage of mean)
    const mean = allPowers.reduce((a, b) => a + b, 0) / allPowers.length;
    const variance =
      allPowers.reduce((sum, power) => sum + Math.pow(power - mean, 2), 0) / allPowers.length;
    const stdDev = Math.sqrt(variance);
    const consistency = mean > 0 ? Math.round(100 - (stdDev / mean) * 100) : 0;

    // Create enhanced rower data
    const enhancedRowerData = Object.entries(rowerPowers).map(([rower, powers]) => {
      const sortedPowers = powers.sort((a, b) => a - b);
      const p25Index = Math.floor(sortedPowers.length * 0.25);
      const p75Index = Math.floor(sortedPowers.length * 0.75);

      // Calculate individual rower consistency
      const rowerMean = powers.reduce((a, b) => a + b, 0) / powers.length;
      const rowerVariance =
        powers.reduce((sum, power) => sum + Math.pow(power - rowerMean, 2), 0) / powers.length;
      const rowerStdDev = Math.sqrt(rowerVariance);
      const rowerConsistency =
        rowerMean > 0 ? Math.round(100 - (rowerStdDev / rowerMean) * 100) : 0;

      return {
        id: Number(rower),
        power: Math.round(powers.reduce((a, b) => a + b, 0) / powers.length),
        min: Math.min(...powers),
        max: Math.max(...powers),
        p25: sortedPowers[p25Index] || 0,
        p75: sortedPowers[p75Index] || 0,
        peak: Math.max(...powers),
        consistency: rowerConsistency,
        powerDistribution: (() => {
          // Calculate power distribution histogram
          const minPower = Math.min(...powers);
          const maxPower = Math.max(...powers);
          const range = maxPower - minPower;
          const binCount = Math.min(20, Math.max(10, Math.floor(powers.length / 3))); // 10-20 bins for smoother gradient
          const binWidth = range / binCount;

          const distribution = [];
          for (let i = 0; i < binCount; i++) {
            const start = minPower + i * binWidth;
            const end = minPower + (i + 1) * binWidth;
            const count = powers.filter(
              (p) => p >= start && p < (i === binCount - 1 ? end + 1 : end)
            ).length;
            const frequency = count / powers.length;

            if (count > 0) {
              // Only include bins with data
              distribution.push({
                start: Math.round(start),
                end: Math.round(end),
                count,
                frequency,
              });
            }
          }
          return distribution;
        })(),
      };
    });

    const sessionDuration =
      sessionStartTime !== Infinity && sessionEndTime !== -Infinity
        ? (sessionEndTime - sessionStartTime) / (1000 * 60) // Convert to minutes
        : 0;

    // Seat ID mapping for this session
    const seatIdMapping = await this.getSeatIdMapping(sessionId);

    // Add cache busting to ensure fresh data
    const result = {
      success: true,
      data: {
        averagePowerByRower,
        overallAverage,
        crewAverage: overallAverage,
        sessionDuration,
        dataPointCount: data.length,
        rowerPowers: {},
        strokeCount: 0,
        individualStrokes: {},
        powerRange,
        peakPower,
        consistency,
        enhancedRowerData,
        seatIdMapping: seatIdMapping,
      },
      overallAverage,
      sessionDuration,
      dataPointCount: data.length,
    };

    DEBUG.log(
      'TELEMETRY',
      '🔍 SWIVEL_AVG_005: Returning 0x800A data with peakPower:',
      peakPower,
      'consistency:',
      consistency
    );
    return result;
  },

  async getSessionRoster(sessionId: string): Promise<SessionRosterEntry[]> {
    try {
      const resolvedId = await this.resolveSessionId(sessionId);
      if (!resolvedId) {
        return [];
      }

      const { data: rows, error } = await supabase
        .from('telemetry_rowers')
        .select(
          'id, session_id, position, seat_id, name, first_name, last_name, roster_id, created_at'
        )
        .eq('session_id', resolvedId)
        .order('position', { ascending: true });

      if (error) {
        throw error;
      }

      const byPosition = new Map<number, SessionRosterEntry>();
      (rows || []).forEach((row: any) => {
        if (typeof row.position !== 'number') {
          return;
        }
        const entry: SessionRosterEntry = {
          id: row.id as string,
          sessionId: row.session_id as string,
          position: row.position as number,
          rosterId: (row.roster_id as string) ?? null,
          seatId: (row.seat_id as string) ?? null,
          firstName: (row.first_name as string) ?? null,
          lastName: (row.last_name as string) ?? null,
          displayName: (row.name as string) ?? null,
          createdAt: (row.created_at as string) ?? null,
        };
        byPosition.set(entry.position, entry);
      });

      const results: SessionRosterEntry[] = [];
      for (let position = 1; position <= 9; position += 1) {
        const existing = byPosition.get(position);
        if (existing) {
          results.push(existing);
        } else {
          results.push({
            id: '',
            sessionId: resolvedId,
            position,
            rosterId: null,
            seatId: null,
            firstName: null,
            lastName: null,
            displayName: null,
            createdAt: null,
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error fetching session roster:', error);
      return [];
    }
  },

  async updateSessionRoster(
    sessionId: string,
    actorId: string,
    changes: SessionRosterChange[]
  ): Promise<SessionRosterUpdateResult> {
    if (!sessionId || !Array.isArray(changes)) {
      throw new Error('Invalid payload for updateSessionRoster');
    }

    if (changes.length === 0) {
      return { success: true, roster: [] };
    }

    // Get auth token from Supabase
    const {
      data: { session: authSession },
    } = await supabase.auth.getSession();

    if (!authSession?.access_token) {
      throw new Error('Not authenticated');
    }

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const apiUrl = isLocalhost
      ? 'http://localhost:3001/api/session-roster/update'
      : '/api/session-roster/update';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authSession.access_token}`,
      },
      body: JSON.stringify({
        sessionId,
        changes,
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const message = errorBody?.error || 'Failed to update session roster';
      throw new Error(message);
    }

    const payload = (await response.json()) as SessionRosterUpdateResult;
    return payload;
  },

  // Update session metadata
  async updateSession(
    sessionId: string,
    updates: {
      sessionName?: string;
      crewName?: string;
      sessionDate?: string;
      startTime?: string;
      location?: string;
      boatType?: string;
    }
  ): Promise<SessionInfo> {
    // Get auth token from Supabase
    const {
      data: { session: authSession },
    } = await supabase.auth.getSession();

    if (!authSession?.access_token) {
      throw new Error('Not authenticated');
    }

    // Call the API endpoint
    const response = await fetch(
      `/api/sessions/update?sessionId=${encodeURIComponent(sessionId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authSession.access_token}`,
        },
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const message = errorBody?.error || 'Failed to update session';
      throw new Error(message);
    }

    const updatedSession = (await response.json()) as SessionInfo;

    await invalidateSessionsListCaches();

    return updatedSession;
  },

  async getSeatIdMapping(sessionId: string): Promise<{ [position: number]: string } | null> {
    try {
      const resolvedId = await this.resolveSessionId(sessionId);
      if (!resolvedId) {
        return null;
      }

      const { data: rowerData, error } = await supabase
        .from('telemetry_rowers')
        .select('position, seat_id, name, first_name, last_name, roster_id')
        .eq('session_id', resolvedId)
        .order('position', { ascending: true });

      if (error) throw error;

      if (!rowerData || rowerData.length === 0) {
        return null;
      }

      const seatIdMapping: { [position: number]: string } = {};
      rowerData.forEach((rower: any) => {
        if (rower.position && rower.seat_id) {
          seatIdMapping[rower.position] = rower.seat_id;
        }
      });

      // Enrich with display names from rower_profiles when roster_id is available
      const rosterIds = rowerData.filter((r: any) => r.roster_id).map((r: any) => r.roster_id);

      let profileNames: Record<string, string> = {};
      if (rosterIds.length > 0) {
        const { data: profiles } = await supabase
          .from('rower_profiles')
          .select('id, preferred_name, first_name, last_name')
          .in('id', rosterIds);

        if (profiles) {
          profiles.forEach((p: any) => {
            const displayName =
              p.preferred_name?.trim() ||
              `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
              null;
            if (displayName) profileNames[p.id] = displayName;
          });
        }
      }

      // Build name mapping: position -> display name
      const nameMapping: { [position: number]: string } = {};
      rowerData.forEach((rower: any) => {
        if (!rower.position) return;
        // Prefer profile name (preferred_name > first+last),
        // then clean first_name+last_name from telemetry_rowers,
        // then telemetry_rowers.name as last resort (may contain concatenated values)
        const profileName = rower.roster_id ? profileNames[rower.roster_id] : null;
        const fullName = `${rower.first_name || ''} ${rower.last_name || ''}`.trim();
        const fallbackName = fullName || rower.name?.trim() || null;
        const displayName = profileName || fallbackName;
        if (displayName) nameMapping[rower.position] = displayName;
      });

      // Attach coach-facing names keyed by position (read by charts via __nameMapping)
      (seatIdMapping as any).__nameMapping = nameMapping;

      return seatIdMapping;
    } catch (error) {
      console.error('Error fetching seat ID mapping:', error);
      return null;
    }
  },

  // Get comprehensive session data for dashboard and session views
  async getComprehensiveSessionData(sessionId: string): Promise<any> {
    return PowerCacheHelper.withCache(`comprehensive-data-${sessionId}`, async () => {
      try {
        const resolvedId = await this.resolveSessionId(sessionId);

        if (!resolvedId) {
          return null;
        }

        // Get basic session info
        const { data: sessionData, error: sessionError } = await supabase
          .from('telemetry_sessions')
          .select('*')
          .eq('id', resolvedId)
          .single();

        if (sessionError) throw sessionError;

        // Extract venue information for GPS filtering
        const sessionVenue = sessionData.venue_name;

        // Get rower information with display names / seat mapping
        const { data: rowerData, error: rowerError } = await supabase
          .from('telemetry_rowers')
          .select('*')
          .eq('session_id', resolvedId)
          .order('position', { ascending: true });

        if (rowerError) throw rowerError;

        // Get GPS data
        const { data: gpsData, error: gpsError } = await supabase
          .from('telemetry_gps')
          .select('*')
          .eq('session_id', resolvedId)
          .order('timestamp', { ascending: true });

        // Check for duplicate timestamps and large jumps
        if (gpsData && gpsData.length > 10) {
          // Show first 10 points
          for (let i = 0; i < 10; i++) {
            DEBUG.log(
              'GPS',
              `  Point ${i}: time=${gpsData[i].timestamp}, lat=${gpsData[i].latitude}, lon=${gpsData[i].longitude}`
            );
          }

          // Check for duplicates
          const timestampCounts: Record<string, number> = {};
          gpsData.forEach((point: any) => {
            const timestamp = String(point.timestamp);
            timestampCounts[timestamp] = (timestampCounts[timestamp] || 0) + 1;
          });
          const duplicates = Object.entries(timestampCounts).filter(([_, count]) => count > 1);
          if (duplicates.length > 0) {
            DEBUG.log('GPS', '⚠️ DUPLICATE TIMESTAMPS FOUND:', duplicates.slice(0, 5));
          }

          // Show details of the duplicates
          if (duplicates.length > 0) {
            DEBUG.log('GPS', '📍 DUPLICATE TIMESTAMP DETAILS:');
            duplicates.slice(0, 3).forEach(([timestamp, count]) => {
              const duplicatePoints = gpsData.filter((p: any) => p.timestamp === timestamp);
              DEBUG.log('GPS', `  Timestamp ${timestamp} has ${count} entries:`);
              duplicatePoints.forEach((p: any, idx: number) => {
                DEBUG.log('GPS', `    ${idx + 1}: lat=${p.latitude}, lon=${p.longitude}`);
              });
            });
          }

          // Check for large coordinate jumps
          let largeJumps: any[] = [];
          for (let i = 1; i < Math.min(gpsData.length, 20); i++) {
            const currentPoint = gpsData[i] as any;
            const prevPoint = gpsData[i - 1] as any;
            const latDiff = Math.abs(Number(currentPoint.latitude) - Number(prevPoint.latitude));
            const lonDiff = Math.abs(Number(currentPoint.longitude) - Number(prevPoint.longitude));
            if (latDiff > 0.001 || lonDiff > 0.001) {
              largeJumps.push({
                from: i - 1,
                to: i,
                latDiff: latDiff.toFixed(6),
                lonDiff: lonDiff.toFixed(6),
              });
            }
          }
          if (largeJumps.length > 0) {
            DEBUG.log('GPS', '⚠️ LARGE COORDINATE JUMPS:', largeJumps);
          }

          // Also show the actual large jumps
          if (largeJumps.length > 0) {
            DEBUG.log('GPS', '📍 LARGE JUMP DETAILS:');
            largeJumps.forEach((jump) => {
              const _from = gpsData[jump.from] as any;
              const _to = gpsData[jump.to] as any;
            });
          }
        }

        if (gpsError) throw gpsError;

        /**
         * CRITICAL GPS DEDUPLICATION ALGORITHM - BAND-AID FIX (Fragility Score: 8/10)
         *
         * PROBLEM: GPS devices sometimes record multiple points with identical timestamps
         * but different coordinates. This creates duplicate entries that must be resolved.
         *
         * CAMBRIDGE-SPECIFIC ASSUMPTIONS (December 2024 band-aid):
         * - River points have higher latitude/longitude values than land points
         * - Rowing club is SW of the river (lat < 52.218, lon < 0.155)
         * - Valid river coordinates: lat > 52.218 AND lon > 0.155
         * - Higher coordinates = closer to river = more accurate for rowing analysis
         *
         * ALGORITHM LOGIC:
         * 1. Group GPS points by timestamp (string key)
         * 2. For duplicate timestamps, prefer point with higher coordinates
         * 3. Prioritize latitude over longitude (OR condition, not AND)
         * 4. Assumption: GPS drift toward land creates lower coordinate duplicates
         *
         * BREAKING POINTS:
         * - Will fail for venues where river is SW/S/SE of starting point
         * - Will fail for venues with different coordinate systems
         * - No validation that "higher" coordinates are actually more accurate
         *
         * WHY THIS IS FRAGILE:
         * - Hardcoded geographic assumptions for Cambridge, UK only
         * - No mathematical basis for coordinate preference logic
         * - Band-aid solution from December 2024 that needs proper fix
         * - Could silently corrupt GPS data for non-Cambridge venues
         */

        // Remove duplicate timestamps - keep the ones on the river (higher lat/lon values)
        let deduplicatedGpsData = gpsData;
        if (gpsData && gpsData.length > 0) {
          const uniqueGPS: Record<string, any> = {};

          gpsData.forEach((point: any) => {
            const key = String(point.timestamp);
            if (!uniqueGPS[key]) {
              uniqueGPS[key] = point;
            } else {
              // BAND-AID LOGIC: Keep the point with higher coordinates (assumed to be on river)
              // WARNING: This assumes river is NE of rowing club (Cambridge-specific)
              const existing = uniqueGPS[key];
              if (
                Number(point.latitude) > Number(existing.latitude) ||
                Number(point.longitude) > Number(existing.longitude)
              ) {
                uniqueGPS[key] = point;
              }
            }
          });

          deduplicatedGpsData = Object.values(uniqueGPS).sort(
            (a: any, b: any) => Number(a.timestamp) - Number(b.timestamp)
          );
        }

        /**
         * CAMBRIDGE-SPECIFIC RIVER FILTERING - HARDCODED COORDINATES
         *
         * PURPOSE: Remove GPS points recorded on land (dock, rowing club building)
         * to keep only points from actual rowing on the river.
         *
         * HARDCODED ASSUMPTIONS FOR CAMBRIDGE, UK:
         * - Cambridge University Boat Club coordinates: ~52.217, 0.154
         * - River Cam flows NE of the boat club
         * - River boundary: latitude > 52.218 AND longitude > 0.155
         * - These coordinates were empirically determined for Cambridge
         *
         * ALGORITHM:
         * - Filter points where BOTH lat > 52.218 AND lon > 0.155
         * - Points below these thresholds assumed to be on land/dock
         *
         * BREAKING POINTS:
         * - Completely wrong for any venue outside Cambridge
         * - No dynamic venue detection or configuration system
         * - Could remove valid river data if coordinates drift slightly
         * - No fallback if all GPS points are filtered out
         *
         * PROPER SOLUTION NEEDED:
         * - Venue-specific coordinate configuration
         * - Dynamic boundary detection based on GPS clustering
         * - Validation that filtered points form coherent track
         */

        // Smart GPS filtering: Support rowing anywhere in the world without hardcoding venues
        if (deduplicatedGpsData && deduplicatedGpsData.length > 0) {
          DEBUG.log('GPS', `🗺️ GPS points before smart filtering: ${deduplicatedGpsData.length}`);

          // Only apply venue-specific filtering for explicitly configured venues
          // Default: Accept all GPS data for worldwide rowing support
          const knownVenuesWithBoundaries = ['cambridge']; // Only venues with specific river boundaries

          const shouldApplyVenueFiltering =
            sessionVenue &&
            typeof sessionVenue === 'string' &&
            knownVenuesWithBoundaries.includes(sessionVenue.toLowerCase());

          if (shouldApplyVenueFiltering) {
            DEBUG.log('GPS', `🗺️ Applying venue-specific filtering for: ${sessionVenue}`);
            const { getVenueConfig } = require('../../config/venues');
            const venueConfig = getVenueConfig(sessionVenue);

            const riverGpsData = deduplicatedGpsData.filter((point: any) => {
              return venueConfig.riverBoundary
                ? venueConfig.riverBoundary.logic(Number(point.latitude), Number(point.longitude))
                : true;
            });

            DEBUG.log('GPS', `🗺️ GPS points after venue filtering: ${riverGpsData.length}`);

            if (riverGpsData.length === 0) {
              DEBUG.log('GPS', '⚠️ Venue filtering removed all points. Using original data.');
              // Keep original data if venue filtering was too aggressive
            } else {
              deduplicatedGpsData = riverGpsData;
            }
          } else {
            DEBUG.log(
              'GPS',
              `🗺️ Worldwide mode: Using all GPS data for venue "${sessionVenue || 'unknown'}"`
            );
            // For new locations worldwide, use all GPS data without filtering
            // This supports rowing clubs globally without requiring hardcoded coordinates
          }

          DEBUG.log('GPS', `🗺️ Final GPS points count: ${deduplicatedGpsData.length}`);
        }

        // Detect actual crew size from telemetry data
        const { data: telemetryData } = await supabase
          .from('telemetry_data')
          .select('rower_position')
          .eq('session_id', resolvedId);

        const actualCrewSize = telemetryData
          ? Array.from(new Set(telemetryData.map((d) => d.rower_position))).filter((p) => p).length
          : rowerData?.length || 0;

        // Get parameter info
        const { data: parameterData, error: parameterError } = await supabase
          .from('telemetry_parameters')
          .select('*')
          .eq('session_id', resolvedId)
          .maybeSingle();

        if (parameterError && parameterError.code !== 'PGRST116') {
          console.error('Error fetching parameter info:', parameterError);
        }

        // Format comprehensive data for dashboard and session views
        return {
          sessionInfo: {
            sessionId: sessionData.session_id,
            date: sessionData.session_date,
            startTime: sessionData.start_time,
            location: sessionData.location || 'Location not specified',
            boatType: sessionData.boat_type || 'Boat type not specified',
            crewCount: actualCrewSize,
          },
          crewInfo: (rowerData || []).map((rower: any) => ({
            position: rower.position?.toString() || '',
            name: rower.name || `Rower ${rower.position}`,
            squad: rower.squad || '',
            side: rower.side || '',
            seatId: rower.seat_id || '',
          })),
          boatInfo: {
            name: 'Boat name not specified',
            manufacturer: 'Manufacturer not specified',
            seats: actualCrewSize?.toString() || '0',
            coxed: actualCrewSize >= 8 ? 'yes' : 'no',
            rig: 'sweep',
          },
          venueInfo: {
            name: sessionData.venue_name || '',
            latitude: sessionData.venue_latitude || '',
            longitude: sessionData.venue_longitude || '',
            altitude: '',
          },
          gpsData: (() => {
            // Calculate total distance from GPS coordinates using Haversine formula
            const calculatedDistance =
              deduplicatedGpsData && deduplicatedGpsData.length > 1
                ? gpsCalculations.calculateDistanceFromGPS(
                    deduplicatedGpsData.map((point: any) => ({
                      latitude: parseFloat(point.latitude),
                      longitude: parseFloat(point.longitude),
                    }))
                  )
                : 0;

            // Calculate speeds from GPS coordinates instead of relying on database values
            const gpsPointsWithSpeed = gpsCalculations.calculateSpeedsFromGPS(
              (deduplicatedGpsData || []).map((point: any) => ({
                latitude: parseFloat(point.latitude),
                longitude: parseFloat(point.longitude),
                timestamp: point.timestamp,
              }))
            );

            return gpsPointsWithSpeed.map((point: any, index: number, array: any[]) => ({
              time: point.timestamp, // timestamp → time
              latitude: point.latitude, // Already parsed as number
              longitude: point.longitude, // Already parsed as number
              speed: point.speed, // Calculated from GPS coordinates
              // Store total calculated distance on the last GPS point, 0 on all others
              distance: index === array.length - 1 ? calculatedDistance : 0,
            }));
          })(),
          parameterInfo: parameterData || null,
        };
      } catch (error) {
        console.error('Error fetching comprehensive session data:', error);
        throw error;
      }
    });
  },

  // Get Parameter Info for specific session
  async getParameterInfo(sessionId: string): Promise<ParameterInfo | null> {
    try {
      const { data, error } = await supabase
        .from('telemetry_parameters')
        .select('*')
        .eq('session_id', sessionId);
      // REMOVED .single() - this was causing 406 errors when no rows exist

      if (error) {
        console.error('Error fetching parameter info:', error);
        // Fall back to sensible defaults so physics can still render
        return {
          id: 'default-params',
          session_id: sessionId,
          // Sweep defaults commonly used in app logic
          sweep_oar_length: 376,
          sweep_oar_inboard: 115,
          sweep_drive_start: 30,
          sweep_recovery: null,
          // Scull left null unless explicitly provided
          scull_oar_length: null,
          scull_oar_inboard: null,
          scull_drive_start: null,
          scull_recovery: null,
          // Optional/derived
          impeller_scaling: null,
          average_rating_over: null,
          created_at: new Date().toISOString(),
        };
      }

      // Handle empty result (no parameter data for this session)
      if (!data || data.length === 0) {
        // Provide default sweep parameters so physics waveform is available
        return {
          id: 'default-params',
          session_id: sessionId,
          sweep_oar_length: 376,
          sweep_oar_inboard: 115,
          sweep_drive_start: 30,
          sweep_recovery: null,
          scull_oar_length: null,
          scull_oar_inboard: null,
          scull_drive_start: null,
          scull_recovery: null,
          impeller_scaling: null,
          average_rating_over: null,
          created_at: new Date().toISOString(),
        };
      }

      // Return first result (should only be one per session)
      return transformToParameterInfo(data[0]);
    } catch (error) {
      console.error('Parameter info query failed:', error);
      // Provide defaults on unexpected failure as well
      return {
        id: 'default-params',
        session_id: sessionId,
        sweep_oar_length: 376,
        sweep_oar_inboard: 115,
        sweep_drive_start: 30,
        sweep_recovery: null,
        scull_oar_length: null,
        scull_oar_inboard: null,
        scull_drive_start: null,
        scull_recovery: null,
        impeller_scaling: null,
        average_rating_over: null,
        created_at: new Date().toISOString(),
      };
    }
  },

  // Utility function to check if session has parameter data
  async hasParameterInfo(sessionId: string): Promise<boolean> {
    const parameters = await this.getParameterInfo(sessionId);
    return parameters !== null;
  },

  // Get parameter info for multiple sessions (for comparison)
  async getParameterInfoForSessions(
    sessionIds: string[]
  ): Promise<Record<string, ParameterInfo | null>> {
    const { data, error } = await supabase
      .from('telemetry_parameters')
      .select('*')
      .in('session_id', sessionIds);

    if (error) {
      console.error('Error fetching multiple parameter info:', error);
      return {};
    }

    // Convert to sessionId -> parameterInfo mapping
    const parameterMap: Record<string, ParameterInfo | null> = {};
    sessionIds.forEach((id) => {
      const rawParam = data?.find((p) => p.session_id === id);
      parameterMap[id] = rawParam ? transformToParameterInfo(rawParam) : null;
    });

    return parameterMap;
  },

  // Get speed profile data for SpeedProfileChart
  async getSpeedProfileData(sessionId: string): Promise<
    Array<{
      timestamp: number;
      speed: number;
      distance: number;
      accel: number;
    }>
  > {
    try {
      const resolvedId = await this.resolveSessionId(sessionId);

      if (!resolvedId) {
        return [];
      }

      const { data, error } = await supabase
        .from('telemetry_gps')
        .select('timestamp, speed, distance')
        .eq('session_id', resolvedId)
        .not('speed', 'is', null)
        .not('distance', 'is', null)
        .order('timestamp', { ascending: true });

      if (error) throw error;

      // Use extracted gpsCalculations module for speed profile processing
      return gpsCalculations.processSpeedProfile(data || []);
    } catch (error) {
      console.error('Error fetching speed profile data:', error);
      return []; // Return empty array instead of throwing
    }
  },

  /** Soft archive or restore visibility (RPC enforces coach access). */
  async setTelemetrySessionArchived(sessionId: string, archived: boolean): Promise<void> {
    const { error } = await supabase.rpc('set_telemetry_session_archived', {
      p_session_id: sessionId,
      p_archived: archived,
    });
    if (error) {
      console.error('set_telemetry_session_archived error:', error);
      throw new Error(error.message || 'Failed to update session archive state');
    }
    await invalidateSessionsListCaches();
  },

  // Delete sessions by IDs (global / no-team sessions only; product policy for coach UI)
  async deleteSessions(sessionIds: string[]): Promise<void> {
    if (sessionIds.length === 0) {
      return;
    }

    try {
      const { data: rows, error: fetchError } = await supabase
        .from('telemetry_sessions')
        .select('id, team_id')
        .in('id', sessionIds);

      if (fetchError) {
        throw fetchError;
      }

      for (const row of rows || []) {
        if (row.team_id != null) {
          throw new Error(
            'Team-associated sessions cannot be permanently deleted from the coach app.'
          );
        }
      }

      const {
        data,
        error,
        count: _count,
      } = await supabase.from('telemetry_sessions').delete().in('id', sessionIds).select();

      if (error) {
        console.error('🚨 DELETE ERROR:', error);
        throw error;
      }

      const deletedCount = data ? data.length : 0;

      if (deletedCount === 0) {
        console.warn('⚠️ INFO: No sessions were deleted - they may have been already deleted');
      } else {
        DEBUG.log('TELEMETRY', `🎉 Successfully deleted ${deletedCount} session(s) from database`);
        try {
          await invalidateSessionsListCaches();
          DEBUG.log('TELEMETRY', '✅ sessions list caches invalidated after deletion');
        } catch (cacheError) {
          console.warn('⚠️ Failed to invalidate sessions list caches:', cacheError);
        }
      }
    } catch (error) {
      console.error('Error deleting sessions:', error);
      throw new Error(
        error instanceof Error ? error.message : 'Failed to delete sessions. Please try again.'
      );
    }
  },

  // Calculate stroke rate from telemetry data based on stroke timing
  async calculateStrokeRate(sessionId: string): Promise<number> {
    try {
      return await PowerCacheHelper.withCache(`stroke-rate-${sessionId}`, async () => {
        try {
          // Resolve session ID
          const resolvedId = await this.resolveSessionId(sessionId);
          if (!resolvedId) {
            DEBUG.log('TELEMETRY', 'Session not found for stroke rate calculation');
            return 0;
          }

          // Get telemetry data with stroke information
          const { data: telemetryData, error } = await supabase
            .from('telemetry_data')
            .select('timestamp, gate_angle, gate_force_x')
            .eq('session_id', resolvedId)
            .order('timestamp', { ascending: true })
            .limit(50000); // Limit to reasonable dataset size

          if (error) {
            console.error('Error fetching telemetry data for stroke rate:', error);
            return 0;
          }

          if (!telemetryData || telemetryData.length < 2) {
            DEBUG.log('TELEMETRY', 'Insufficient data for stroke rate calculation');
            return 0;
          }

          // Use extracted strokeAnalysis module for pure calculation
          return strokeAnalysis.calculateStrokeRate(telemetryData);
        } catch (error) {
          console.error('Error calculating stroke rate:', error);
          return 0;
        }
      });
    } catch (cacheError) {
      console.error('Error with Redis cache for stroke rate:', cacheError);
      return 0;
    }
  },

  // Calculate average stroke rate across a time range
  async calculateAverageStrokeRate(
    sessionId: string,
    startTime: string,
    endTime: string
  ): Promise<number> {
    try {
      return await PowerCacheHelper.withCache(
        `avg-stroke-rate-${sessionId}-${startTime}-${endTime}`,
        async () => {
          try {
            // Resolve session ID
            const resolvedId = await this.resolveSessionId(sessionId);
            if (!resolvedId) {
              DEBUG.log('TELEMETRY', 'Session not found for average stroke rate calculation');
              return 0;
            }

            // Get telemetry data within time range
            const { data: telemetryData, error } = await supabase
              .from('telemetry_data')
              .select('timestamp, gate_angle, gate_force_x')
              .eq('session_id', resolvedId)
              .gte('timestamp', startTime)
              .lte('timestamp', endTime)
              .order('timestamp', { ascending: true })
              .limit(50000);

            if (error) {
              console.error('Error fetching telemetry data for average stroke rate:', error);
              return 0;
            }

            if (!telemetryData || telemetryData.length < 2) {
              DEBUG.log('TELEMETRY', 'Insufficient data for average stroke rate calculation');
              return 0;
            }

            // Calculate stroke rate in segments and average them
            const segmentSize = Math.max(100, Math.floor(telemetryData.length / 10)); // 10 segments
            const segmentRates: number[] = [];

            for (let i = 0; i < telemetryData.length; i += segmentSize) {
              const segmentData = telemetryData.slice(i, i + segmentSize);
              if (segmentData.length < 2) continue;

              const strokeIntervals: number[] = [];
              let lastStrokeTime: number | null = null;

              for (const point of segmentData) {
                const currentTime = new Date(point.timestamp as string).getTime();

                const gateForce = point.gate_force_x;
                if (gateForce && typeof gateForce === 'number' && gateForce > 2) {
                  if (lastStrokeTime !== null) {
                    const intervalMs = currentTime - lastStrokeTime;
                    if (intervalMs > 500 && intervalMs < 5000) {
                      strokeIntervals.push(intervalMs);
                    }
                  }
                  lastStrokeTime = currentTime;
                }
              }

              if (strokeIntervals.length > 0) {
                const averageIntervalMs =
                  strokeIntervals.reduce((sum, interval) => sum + interval, 0) /
                  strokeIntervals.length;
                const segmentRate = (60 * 1000) / averageIntervalMs;
                segmentRates.push(segmentRate);
              }
            }

            if (segmentRates.length === 0) {
              DEBUG.log('TELEMETRY', 'No valid segment rates calculated');
              return 0;
            }

            // Return average of all segment rates
            const averageStrokeRate =
              segmentRates.reduce((sum, rate) => sum + rate, 0) / segmentRates.length;
            DEBUG.log(
              'TELEMETRY',
              `Calculated average stroke rate: ${averageStrokeRate.toFixed(1)} SPM from ${segmentRates.length} segments`
            );
            return Math.round(averageStrokeRate);
          } catch (error) {
            console.error('Error calculating average stroke rate:', error);
            return 0;
          }
        }
      );
    } catch (cacheError) {
      console.error('Error with Redis cache for average stroke rate:', cacheError);
      return 0;
    }
  },

  // Calculate efficiency from telemetry data based on force consistency
  async calculateEfficiency(sessionId: string): Promise<number> {
    try {
      return await PowerCacheHelper.withCache(
        `efficiency-power-transfer-${sessionId}`,
        async () => {
          try {
            // Resolve session ID
            const resolvedId = await this.resolveSessionId(sessionId);
            if (!resolvedId) {
              DEBUG.log('TELEMETRY', 'Session not found for efficiency calculation');
              return 0;
            }

            // Get telemetry data with force and angle information
            const { data: telemetryData, error } = await supabase
              .from('telemetry_data')
              .select('timestamp, rower_position, gate_force_x, gate_angle_velocity, gate_angle')
              .eq('session_id', resolvedId)
              .order('timestamp', { ascending: true })
              .limit(50000); // Limit to reasonable dataset size

            if (error) {
              console.error('Error fetching telemetry data for efficiency:', error);
              return 0;
            }

            if (!telemetryData || telemetryData.length < 4) {
              DEBUG.log('TELEMETRY', 'Insufficient data for efficiency calculation');
              return 0;
            }

            // Get oar parameters for drive phase detection
            const parameterInfo = await this.getParameterInfo(resolvedId);

            // Use extracted efficiencyMetrics module for pure calculation
            const efficiency = efficiencyMetrics.calculateEfficiency(telemetryData, parameterInfo);

            DEBUG.log(
              'TELEMETRY',
              `[KIRO-EFFICIENCY-TEST] Raw efficiency from efficiencyMetrics: ${efficiency}`
            );
            DEBUG.log(
              'TELEMETRY',
              `[KIRO-EFFICIENCY-TEST] Efficiency as percentage: ${(efficiency * 100).toFixed(1)}%`
            );

            DEBUG.log('TELEMETRY', `Calculated efficiency: ${(efficiency * 100).toFixed(1)}%`);
            return efficiency;
          } catch (error) {
            console.error('Error calculating efficiency:', error);
            return 0;
          }
        }
      );
    } catch (cacheError) {
      console.error('Error with Redis cache for efficiency:', cacheError);
      return 0;
    }
  },

  // Calculate average efficiency across a time range
  async calculateAverageEfficiency(
    sessionId: string,
    startTime: string,
    endTime: string
  ): Promise<number> {
    try {
      return await PowerCacheHelper.withCache(
        `avg-efficiency-${sessionId}-${startTime}-${endTime}`,
        async () => {
          try {
            // Resolve session ID
            const resolvedId = await this.resolveSessionId(sessionId);
            if (!resolvedId) {
              DEBUG.log('TELEMETRY', 'Session not found for average efficiency calculation');
              return 0;
            }

            // Get telemetry data within time range
            const { data: telemetryData, error } = await supabase
              .from('telemetry_data')
              .select('timestamp, rower_position, gate_force_x, gate_angle_velocity, gate_angle')
              .eq('session_id', resolvedId)
              .gte('timestamp', startTime)
              .lte('timestamp', endTime)
              .order('timestamp', { ascending: true })
              .limit(50000);

            if (error) {
              console.error('Error fetching telemetry data for average efficiency:', error);
              return 0;
            }

            if (!telemetryData || telemetryData.length < 4) {
              DEBUG.log('TELEMETRY', 'Insufficient data for average efficiency calculation');
              return 0;
            }

            // Calculate efficiency in segments and average them
            const segmentSize = Math.max(200, Math.floor(telemetryData.length / 10)); // 10 segments
            const segmentEfficiencies: number[] = [];

            for (let i = 0; i < telemetryData.length; i += segmentSize) {
              const segmentData = telemetryData.slice(i, i + segmentSize);
              if (segmentData.length < 4) continue;

              // Use same efficiency calculation logic for segment
              const rowerData: { [position: number]: any[] } = {};
              for (const point of segmentData) {
                const position = (point.rower_position as number) || 1;
                if (!rowerData[position]) {
                  rowerData[position] = [];
                }
                rowerData[position].push(point);
              }

              const rowerEfficiencies: number[] = [];
              const driveStartAngle = 30; // Default drive start angle

              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              for (const [_position, data] of Object.entries(rowerData)) {
                if (data.length < 3) continue;

                const drivePhaseData = data.filter((point) => {
                  const angle = point.gate_angle || 0;
                  return Math.abs(angle - driveStartAngle) <= 45;
                });

                if (drivePhaseData.length < 2) continue;

                const forces = drivePhaseData
                  .map((point) => point.gate_force_x)
                  .filter((force) => force !== null && force !== undefined && force > 0);

                if (forces.length < 2) continue;

                const mean = forces.reduce((sum, force) => sum + force, 0) / forces.length;
                const variance =
                  forces.reduce((sum, force) => sum + Math.pow(force - mean, 2), 0) / forces.length;
                const standardDeviation = Math.sqrt(variance);
                const coefficientOfVariation = mean > 0 ? standardDeviation / mean : 1;
                const cappedCV = Math.min(coefficientOfVariation, 1.0);
                const rowerEfficiency = (1 - cappedCV) * 100;

                rowerEfficiencies.push(Math.max(0, rowerEfficiency));
              }

              if (rowerEfficiencies.length > 0) {
                const segmentEfficiency =
                  rowerEfficiencies.reduce((sum, eff) => sum + eff, 0) / rowerEfficiencies.length;
                segmentEfficiencies.push(segmentEfficiency / 100); // Convert to 0-1 scale
              }
            }

            if (segmentEfficiencies.length === 0) {
              DEBUG.log('TELEMETRY', 'No valid segment efficiencies calculated');
              return 0;
            }

            // Return average of all segment efficiencies
            const averageEfficiency =
              segmentEfficiencies.reduce((sum, eff) => sum + eff, 0) / segmentEfficiencies.length;
            DEBUG.log(
              'TELEMETRY',
              `Calculated average efficiency: ${(averageEfficiency * 100).toFixed(1)}% from ${segmentEfficiencies.length} segments`
            );
            return Math.round(averageEfficiency * 1000) / 1000; // Round to 3 decimal places
          } catch (error) {
            console.error('Error calculating average efficiency:', error);
            return 0;
          }
        }
      );
    } catch (cacheError) {
      console.error('Error with Redis cache for average efficiency:', cacheError);
      return 0;
    }
  },

  // Debug method to check for stroke metrics (0x800A) data
  async checkStrokeMetricsData(sessionId: string): Promise<void> {
    const resolvedId = await this.resolveSessionId(sessionId);

    if (!resolvedId) {
      return;
    }

    // Check telemetry_stroke_metrics table for 0x800A data
    const {
      data: strokeMetrics,
      error: _error,
      count: _count,
    } = await supabase
      .from('telemetry_stroke_metrics')
      .select('*', { count: 'exact' })
      .eq('session_id', resolvedId)
      .limit(3);

    if (strokeMetrics && strokeMetrics.length > 0) {
    } else {
    }
  },

  // Update session's team_id
  async updateSessionTeam(
    sessionId: string,
    teamId: string | null
  ): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('telemetry_sessions')
        .update({ team_id: teamId })
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating session team:', error);
        return { error };
      }

      await invalidateSessionsListCaches();

      return { error: null };
    } catch (error) {
      console.error('Error updating session team:', error);
      return { error: error as Error };
    }
  },

  // Update session's boat_id
  async updateSessionBoat(
    sessionId: string,
    boatId: string | null
  ): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('telemetry_sessions')
        .update({ boat_id: boatId })
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating session boat:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Error updating session boat:', error);
      return { error: error as Error };
    }
  },
};

// Add missing efficiency calculation method to telemetryService
export const telemetryServiceExtensions = {
  // Calculate efficiency from session data
  async calculateEfficiency(sessionId: string): Promise<number> {
    const resolvedId = await telemetryService.resolveSessionId(sessionId);
    if (!resolvedId) return 0;

    try {
      // Get power data for efficiency calculation
      const result = await telemetryService.getFullSessionAveragePower(sessionId);

      if (result.success && result.data) {
        // Calculate efficiency based on power consistency and crew balance
        const consistency = result.data.consistency || 0;
        const powerRange = result.data.powerRange;

        if (powerRange && powerRange.max > 0) {
          // Efficiency based on power distribution and consistency
          const rangeEfficiency = (1 - (powerRange.max - powerRange.min) / powerRange.max) * 100;
          const overallEfficiency = (consistency + rangeEfficiency) / 2;
          return Math.round(overallEfficiency) / 100; // Return as decimal (0.36 for 36%)
        }
      }

      return 0.36; // Fallback to analyzed value (36%)
    } catch (error) {
      console.error('Error calculating efficiency:', error);
      return 0.36; // Fallback to analyzed value
    }
  },
};

// Contract wrapper methods for platform integration
const telemetryServiceContractExtensions = {
  /**
   * Fetches power time series data and returns it as a validated contract
   * @param sessionId - The session ID to fetch data for
   * @param targetPoints - Number of sample points (default: 1200)
   * @returns Validated PowerTimeSeriesContract or null if fetch fails
   */
  async getPowerTimeSeriesContract(
    this: typeof telemetryService,
    sessionId: string,
    targetPoints: number = 1200
  ): Promise<PowerTimeSeriesContract | null> {
    try {
      const result = await this.getSampledPowerTimeSeries(sessionId, targetPoints);

      if (!result.success || !result.data || result.data.length === 0) {
        return null;
      }

      // Build and validate contract
      return buildPowerTimeSeriesContract({
        sessionId,
        samples: result.data,
        meta: {
          source: 'sampled_rpc',
          length: result.data.length,
        },
      });
    } catch (error) {
      console.error('Error building PowerTimeSeriesContract:', error);
      return null;
    }
  },

  /**
   * Fetches gate force profile data and returns it as a validated contract
   * @param sessionId - The session ID to fetch data for
   * @param options - Optional configuration (rowerPositions, targetPoints, minPointsPerStroke)
   * @returns Validated GateForceProfileContract or null if fetch fails
   */
  async getGateForceProfileContract(
    this: typeof telemetryService,
    sessionId: string,
    options?: { rowerPositions?: number[]; targetPoints?: number; minPointsPerStroke?: number }
  ): Promise<GateForceProfileContract | null> {
    try {
      const result = await this.getGateForceMedianProfile(sessionId, options);

      if (!result || !result.series) {
        return null;
      }

      // Build and validate contract
      return buildGateForceProfileContract({
        sessionId,
        series: result.series,
        meta: {
          targetPoints: result.meta.targetPoints,
          source: result.meta.source,
        },
      });
    } catch (error) {
      console.error('Error building GateForceProfileContract:', error);
      return null;
    }
  },
};

// Extend telemetryService with new methods (only efficiency, keep original calculateStrokeRate)
Object.assign(telemetryService, telemetryServiceExtensions);

// Extend telemetryService with contract methods for platform integration
Object.assign(telemetryService, telemetryServiceContractExtensions);

// Export type that includes all extensions
export type TelemetryServiceWithExtensions = typeof telemetryService &
  typeof telemetryServiceExtensions &
  typeof telemetryServiceContractExtensions;

// Re-export default and as typed instance for imports
export default telemetryService as TelemetryServiceWithExtensions;
export { telemetryService as telemetryServiceTyped };
